import { format, parseISO, isBefore } from 'date-fns'
import { ExclamationIcon } from "@heroicons/react/solid";

import { Header } from '@/components/layout/header'
import { ContentComponents } from '@/components/layout/content-components'
import { Footer } from '@/components/layout/footer'
import { PreviewAlert } from '@/components/shared/preview-alert'
import Image from 'next/image';
import { urlFor } from '@/lib/sanity'
import { Linker } from '@/components/content/linker';
import { HiArrowNarrowLeft } from "react-icons/hi";
import { HiChevronRight } from "react-icons/hi";
import { HiChevronLeft } from "react-icons/hi";

import { EventVirtualLink } from '@/components/content/event/virtual-link';
import { EventAccessLink } from '@/components/content/event/access-link';
import { EventAccessTicket } from '@/components/content/event/access-ticket';
import { EventDateTime } from '@/components/content/event/date-time';
import { EventLocation } from '@/components/content/event/location';
import { SocialShare } from '@/components/content/social-share'

import AddToCalendar from '@culturehq/add-to-calendar'
import console from 'node:console';

const ImageComponent = ({ image, imageBlur, imageAltText, adjustedHeight, adjustedWidth }) => {
  return (<Image
    src={image}
    alt={imageAltText || ''}
    blurDataURL={imageBlur || ''}
    placeholder={imageBlur ? 'blur' : 'empty'}
    layout="fill"
    className='object-cover w-full h-full'
  />
  )
}

function Figure(props: { image?: any; alt?: any; }) {
  const image = props?.image.asset;
  const { alt } = props;
  // const { aspectRatio } = getImageDimensions(image)
  const adjustedWidth = 1960;
  const adjustedHeight = 384 * 2;
  // const adjustedHeight = Math.round(adjustedWidth / aspectRatio);

  const imageScaled = (val: any) => {
    return val ? `${urlFor(val).auto('format').width(adjustedWidth).height(adjustedHeight).url()}` : null
  }
  const imageBlur = (val: any) => {
    return val ? `${urlFor(val).auto('format').width(10).url()}` : null
  }

  return <figure className='relative w-full h-full'><ImageComponent image={imageScaled(image)} imageBlur={imageBlur(image)} imageAltText={alt} adjustedWidth={adjustedWidth} adjustedHeight={adjustedHeight} /></figure>
}

export function Layout({ preview, siteNav, topNav, topFooterNav, bottomFooterNav, siteSettings, asPath, contentComponents, view, event }) {

  let tagList: any;
  if (event?.tags && event.tags.length >= 1) {
    tagList = event.tags.map(tag => tag.title).join(',');
  } else {
    tagList = null;
  }
  const siteName = siteSettings?.title || null;
  const shareTitle = event?.title || null;
  const shareSummary = event?.summary || null;
  const twitterUsername = siteSettings?.socialLinks?.find(account => account?.socialType === "twitter")?.siteLink?.replace('https://twitter.com/', '') || null

  const url = `${process.env.NEXT_PUBLIC_DOMAIN}/events/${event.slug}`

  let shareImage: string;
  if (event?.seoSettings?.openGraphImage?.asset) {
    shareImage = urlFor(event?.seoSettings?.openGraphImage?.asset).auto('format').width(1200).height(630).url()
  } else if (event?.mainImage?.image?.asset) {
    shareImage = urlFor(event?.mainImage?.image?.asset).auto('format').width(1200).height(630).url()
  } else {
    shareImage = null
  }
  const physicalLocation = `${event?.location?.organization} ${event?.location?.address} ${event?.location?.city} ${event?.location?.state} ${event?.location?.zip}`;
  return (
    <div className="flex flex-col min-h-screen">

      {preview && <PreviewAlert asPath={asPath} />}

      <Header siteNav={siteNav} topNav={topNav} siteSettings={siteSettings} />

      {(!event?.hideMainImage) && event?.mainImage?.image &&
        <div className="relative w-full overflow-hidden h-96">
          <Figure {...event.mainImage} />
        </div>
      }

      <div className=" inner-wrapper">



        {/* Title, Category and Date & Times */}
        <div className="mt-8">
          <p className="block mb-2 text-sm font-medium tracking-widest text-gray-500 uppercase">
            <Linker url={`/events/category/${event?.category?.slug}`} classes="hover:underline">
              {event?.category?.title}
            </Linker>
          </p>

          <div className='relative flex mb-8 prose 2xl:-ml-8 md:prose-lg lg:prose-xl prose-h1:mb-0 md:prose-h1:mb-0 lg:prose-h1:mb-0'>
            <h1 className='relative order-2'>{event.title}</h1>
            <Linker url={`/events`} aria-label="Back to the main events page." classes='inline-block pr-3 py-3 transition-all opacity-50 hover:opacity-100 order-1'>
              <HiArrowNarrowLeft />
            </Linker>
          </div>

          <div className="flex flex-col justify-between max-w-2xl md:flex-row">

          <div className="flex flex-col order-2 mb-8 space-y-8 md:space-x-10 md:space-y-0 md:flex-row md:order-1">
            {event?.startDate?.utc && <EventDateTime dateTime={event?.startDate?.utc} title="Starts at" />}
            {event?.endDate?.utc && <EventDateTime dateTime={event?.endDate?.utc} title="Ends at" />}
          </div>

          {/* TODO: */}
            {/* Buy Tickets Online - Links */}
            <div className="order-1 mb-8 md:order-2">
            {(event?.access === 'links' && event?.accessLinks?.length >= 1) && <EventAccessLink links={event?.accessLinks} />}
            </div>

          </div>


          {(!isBefore(new Date(), new Date(parseISO(event?.startDate?.utc)))) &&
            <span className="md:ml-auto inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-600 text-white space-x-2 flex uppercase mb-2">
              <ExclamationIcon className='w-4' />
              <span>Past Event</span>
            </span>}


        </div>

        <div className="grid-cols-1 lg:grid md:gap-6 lg:grid-flow-col-dense lg:grid-cols-4 lg:gap-x-12 lg:gap-y-12">

          <aside className="top-0 lg:max-h-screen lg:sticky lg:col-start-4 lg:col-span-1 md:mt-">




            {(event?.startDate?.utc && event?.endDate?.utc) && <AddToCalendar
              event={{
                name: event?.title,
                details: event?.summary,
                location: physicalLocation,
                startsAt: event?.startDate?.utc,
                endsAt: event?.endDate?.utc
              }}
            />}

            {(event?.type === 'inPerson' || event?.type === 'both' || event?.type === 'virtual') &&
              <div className="flex flex-col mb-8 space-y-8 md:space-x-10 md:space-y-0 lg:space-y-8 lg:space-x-0 md:flex-row lg:flex-col">
                {/* In-Person & Location */}
                {((event?.type === 'inPerson' && event?.location) || (event?.type === 'both' || event?.type === 'inPerson')) && <EventLocation location={event?.location} />}
                {/* Virtual Link */}
                {(((event?.type === 'both' || event?.type === 'virtual') && (event?.virtualLinks && event?.virtualLinks.length >= 1)) && <EventVirtualLink links={event?.virtualLinks} />)}
              </div>}

            {/* Tickets (links or products) */}
            {(event?.access === 'links' || event?.access === 'tickets') &&
              <div className="flex flex-col mb-10 space-y-8 md:space-x-10 md:space-y-0 lg:space-y-8 lg:space-x-0 md:flex-row lg:flex-col">

                {/* Buy Tickets Online - Links */}
                {/* {(event?.access === 'links' && event?.accessLinks?.length >= 1) && <EventAccessLink links={event?.accessLinks} />} */}

                {/* Buy Tickets Online - Buy on the site */}
                {(event?.access === 'tickets' && event?.products?.length >= 1) && <EventAccessTicket products={event?.products} image={urlFor(event?.mainImage?.image?.asset).auto('format').width(100).height(100).url()}
                  url={`${process.env.NEXT_PUBLIC_DOMAIN}/events/${event.slug}`} />}
              </div>}

          </aside>


          <div className="relative lg:col-start-1 lg:col-span-3">
            <div className="-mb-8 -ml-4">
              <ContentComponents contentComponents={contentComponents} view={view} layout={true} />
            </div>
          </div>

        </div>
      </div>





      <div className='w-full mb-0 inner-wrapper'>
        {(event?.tags && event?.tags.length >= 1) && <div className='flex flex-wrap items-baseline mb-8'>
          {/* <p className='inline-block p-0 mr-4 text-xs font-semibold text-gray-400 uppercase'>Tags</p> */}
          <ul className="flex flex-wrap items-center justify-starts">
            {event?.tags?.map((tag, i) =>
            (<li key={tag._id} className="">
              <Linker url={`/events/tag/${tag.slug}`} classes="transition-all hover:transition-all text-xs mr-3 py-1.5 px-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 hover:text-gray-800"> #{tag.slug} </Linker>
            </li>)
            )}
          </ul></div>}

        <SocialShare
          siteName={siteName}
          shareTitle={shareTitle}
          shareSummary={shareSummary}
          shareImage={shareImage}
          tagList={tagList}
          twitterUsername={twitterUsername}
          url={url}
          updatedAt={event?.updatedAt}
        />
      </div>

      {(event?.nextEvent || event?.previousEvent) &&
        <div className='w-full mb-12 inner-wrapper'>
          <div className="flex flex-row flex-wrap items-center justify-between w-full md:space-y-0">
            {event?.previousEvent &&
              <Linker url={`/events/${event?.previousEvent?.slug}`} classes="flex flex-col md:items-center md:flex-row max-w-[40%] transition-all group hover:text-gray-900 space-x-reverse space-x-4 space-y-reverse space-y-2 md:space-y-0 ">
                <span className='order-2 block text-sm text-gray-600 truncate group-hover:text-gray-900'>{event?.previousEvent?.title}</span>
                <span className='flex items-center order-1 text-xs text-gray-700 group-hover:text-gray-900'>
                  <span className='order-2 font-semibold' > previous event</span>
                  <span className='relative order-1 text-2xl group-hover:text-indigo-600'><HiChevronLeft /></span>
                </span>
              </Linker>
            }
            {event?.nextEvent &&
              <Linker url={`/events/${event?.nextEvent?.slug}`} classes="flex flex-col md:space-x-6 space-y-2 space-x-reverse space-y-reverse md:space-x-6 md:text-right md:items-center md:flex-row md:space-y-0 max-w-[40%] transition-all group hover:text-gray-900 ml-auto text-right">
                <span className='order-2 block text-sm text-gray-600 truncate md:order-1 group-hover:text-gray-900'>{event?.nextEvent?.title}</span>
                <span className='flex items-center justify-end order-1 text-xs text-gray-700 md:order-2 group-hover:text-gray-900'>
                  <span className='order-1 font-semibold' > next event</span>
                  <span className='relative order-2 text-2xl group-hover:text-indigo-600'><HiChevronRight /></span>
                </span>
              </Linker>
            }
          </div>
        </div>}

      <Footer siteSettings={siteSettings} topFooterNav={topFooterNav} bottomFooterNav={bottomFooterNav} />
    </div>
  )
}
